import React from "react";
import { ApparelProduct } from "@/types";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit, Trash, ShirtIcon } from "lucide-react";

interface ProductCardProps {
  product: ApparelProduct;
  onEdit?: (product: ApparelProduct) => void;
  onDelete?: (product: ApparelProduct) => void;
  onSelect?: (product: ApparelProduct) => void;
  showAdminControls?: boolean;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onEdit,
  onDelete,
  onSelect,
  showAdminControls = false,
}) => {
  const formatPrice = (priceCents: number) => {
    if (priceCents === 0) {
      return "Included";
    }
    return `$${(priceCents / 100).toFixed(2)}`;
  };

  const getPriceColor = (priceCents: number) => {
    if (priceCents === 0) {
      return "text-green-600 font-medium";
    }
    return "text-gray-900 font-semibold";
  };

  return (
    <Card 
      className="group overflow-hidden border border-gray-200 rounded-xl transition-all duration-300 hover:shadow-lg hover:border-blue-200 cursor-pointer bg-white"
      onClick={() => onSelect?.(product)}
    >
      <CardHeader className="p-0">
        <div className="aspect-square overflow-hidden bg-gradient-to-br from-blue-100 via-indigo-100 to-purple-100 flex items-center justify-center">
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="flex items-center justify-center w-full h-full">
              <ShirtIcon className="h-16 w-16 text-indigo-600" />
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <h3 className="font-semibold text-gray-900 text-lg leading-tight">{product.name}</h3>
            {product.category && (
              <Badge 
                variant="secondary"
                className="bg-blue-100 text-blue-700 border-0 text-xs font-medium"
              >
                {product.category}
              </Badge>
            )}
          </div>
          <p className={`text-lg ${getPriceColor(product.price_cents)}`}>
            {formatPrice(product.price_cents)}
          </p>
        </div>
        
        {showAdminControls && (
          <div className="flex gap-2 pt-2 border-t border-gray-100">
            {onEdit && (
              <Button
                size="sm"
                variant="outline"
                className="flex-1 border-blue-200 text-blue-700 hover:bg-blue-50 hover:border-blue-300"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(product);
                }}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
            {onDelete && (
              <Button 
                size="sm" 
                variant="outline" 
                className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(product);
                }}
              >
                <Trash className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
